import { useCallback, useContext, useEffect, useRef, useState } from "react"
import { get, post } from "helpers/api_helper"
import { store as socketStore } from "components/Common/Socket/store"
import { newPlayerOperationId } from "pages/UserAccounts/PlayerAccounts/playerInvitationState"
import { runCompetitionOperation } from "pages/EventsManagement/components/Tabs/Competitions/competitionOperation"

export const friendlyMessage = code => ({
  INVALID_FRIENDLY_DELIVERY_RETRY: "This notification is no longer eligible for retry. Refresh its delivery status.",
  ERROR_PERMISSIONS: "Your account no longer has permission for this action.",
  FRIENDLY_VERSION_CONFLICT: "This match changed. Review the current match before saving again.",
  FRIENDLY_MATCH_NOT_FOUND: "This match is no longer available.",
  INVALID_FRIENDLY_ACTION: "This action is not available for the current match status.",
  INVALID_FRIENDLY_SCORE: "Review the score and tiebreak values (whole numbers from 0 to 24).",
  INVALID_FRIENDLY_CHANGES: "Review the editable result fields.",
  INVALID_FRIENDLY_RANKED_ELIGIBILITY: "The recorded player levels do not meet ranked match eligibility.",
  INVALID_FRIENDLY_INCOMPLETE_RESULT: "A completed match requires a complete valid result.",
  INVALID_FRIENDLY_OPERATION: "Review the reason and current match before trying again.",
  FRIENDLY_RESULT_UNVERIFIED: "The original ranking result cannot be verified. No changes were applied.",
  FRIENDLY_RESULT_AMBIGUOUS: "More than one ranking result may belong to this match. No changes were applied.",
}[code] || "The action could not be confirmed. Check its saved result before trying again.")

export default function useFriendlyOperation({ userId, matchId, deliveryRetry = false }) {
  const { state: { socket: { socket, socketClientId } = {} } = {} } = useContext(socketStore)
  const key = `${deliveryRetry ? "friendly-delivery-retry" : "friendly-operation"}:${userId}:${matchId}`
  const active = useRef(null), retained = useRef(null)
  const [state, setState] = useState({})
  const persist = useCallback(record => {
    try { sessionStorage.setItem(key, JSON.stringify(record)); return true } catch { return false }
  }, [key])
  // A 4xx on the first send was refused before anything was queued, so the runner ends it as an error with words; Check saved
  // result (firstSubmission false) cannot rule out an earlier commit and stays unknown.
  const observe = useCallback((operation, submit, firstSubmission = false) => {
    active.current?.runner.cancel()
    const runner = runCompetitionOperation({ operation, scopeKeys: ["matchId"], socket, submit, firstSubmission,
      responseTopic: "accounts-response", operationKind: "friendly-match-managed", messageForCode: friendlyMessage,
      read: id => get(`/admin/friendly-match-operations/${matchId}/${id}`) })
    const request = { operation, runner }; active.current = request; retained.current = operation
    setState({ status: "pending", operation })
    runner.promise.then(result => {
      if (active.current !== request || result.status === "cancelled") return
      setState({ ...result, operation })
      if (result.status === "saved") {
        active.current = null; retained.current = null
        try { sessionStorage.removeItem(key) } catch { /* The server retains the confirmed result. */ }
      } else if (result.status === "error") {
        active.current = null
        retained.current = { ...operation, recovery: result }
        persist(retained.current)
      }
    })
  }, [key, matchId, socket, persist])
  useEffect(() => {
    setState({}); retained.current = null
    if (userId) try {
      const op = JSON.parse(sessionStorage.getItem(key) || "null")
      if (op?.matchId === matchId && /^[a-zA-Z0-9-]{16,128}$/.test(op.operationId || "") && op.correlationId && op.payload && (deliveryRetry ? op.action === "retryNotifications" : ["edit", "delete"].includes(op.action))) {
        retained.current = op
        if (op.recovery?.status === "error") setState({ ...op.recovery, operation: op })
        else observe(op)
      }
    } catch { /* Invalid storage cannot initiate a write. */ }
    return () => { active.current?.runner.cancel(); active.current = null }
  }, [userId, key, matchId, observe, deliveryRetry])
  const send = op => () => post(`/admin/friendly-match-operations/${matchId}`, { ...op.payload, operationId: op.operationId, socketClientId }, { headers: { "X-Correlation-Id": op.correlationId } })
  const save = payload => {
    if (!userId || active.current || retained.current) return
    const operation = { matchId, action: payload.action, operationId: newPlayerOperationId(), correlationId: newPlayerOperationId(), payload }
    if (!persist(operation)) { setState({ status: "error", message: "Enable session storage to recover a save after reload." }); return }
    observe(operation, send(operation), true)
  }
  const review = () => {
    if (active.current || retained.current && retained.current.recovery?.status !== "error") return
    try { sessionStorage.removeItem(key) } catch { return }
    retained.current = null; setState({})
  }
  return { ...state, save, review, pending: ["pending", "unknown"].includes(state.status),
    recover: () => state.operation && ["pending", "unknown"].includes(state.status) && observe(state.operation, send(state.operation)) }
}
